import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  BidReportQuestionGroupsQuery,
  BidReportQuestionGroupsQueryVariables,
} from "lib/generated/app-service-gql/graphql";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "lib/hooks/api/utils";
import { BidReportType, inferEnumType } from "lib/types/graphQLEnums";
import { useGraphQlQuery } from "../../useGraphQlClient";

export const bidQuestionsQueryKey = ({
  teamId,
  reportType,
}: {
  teamId: string | undefined;
  reportType?: BidReportType | null;
}) => ["bid_report_questions", teamId ?? null, reportType ?? null];

const bidReportQuestionGroupsQuery = graphql(`
  query bidReportQuestionGroups($request: BidReportQuestionGroupsRequest!) {
    bidReportQuestionGroups(BidReportQuestionGroupsRequest: $request) {
      groups {
        name
        questions {
          id
          title
          question
          groupName
          order
          teamId
          companyId
          autoQualify
          reportType
        }
      }
    }
  }
`);

type GetQuestionsByTeamResponse = BidReportQuestionGroupsQuery["bidReportQuestionGroups"];
export type BidQuestionGroupDto = GetQuestionsByTeamResponse["groups"][0];
export type BidQuestionObj = BidQuestionGroupDto["questions"][0];

export function useBidQuestions(
  reportType: BidReportType,
  options?: UseQueryOptions<
    BidReportQuestionGroupsQuery,
    unknown,
    BidReportQuestionGroupsQuery,
    QueryKey
  >,
) {
  const { data: currentUser } = useCurrentUser();
  const teamId = currentUser?.team.id;
  const { data, ...rest } = useGraphQlQuery<
    BidReportQuestionGroupsQuery,
    BidReportQuestionGroupsQueryVariables
  >(
    bidQuestionsQueryKey({ teamId, reportType }),
    bidReportQuestionGroupsQuery,
    [
      {
        request: {
          reportType: inferEnumType(reportType),
        },
      },
    ],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
      enabled: !!teamId,
    },
  );

  return { data: data?.bidReportQuestionGroups, ...rest };
}
